import { supabase } from "@/integrations/supabase/client";
import logger from "@/lib/logger";
import { toast } from "@/hooks/use-toast";
import { checkInternetConnection } from "@/lib/network-utils";

interface GenerateProfileResult {
  overview: string;
  error: string | null;
  isQuotaExceeded: boolean;
  isRateLimited: boolean;
}

// Function to record profile overview usage in the database
const recordProfileUsage = async () => {
  try {
    // Get current authenticated user
    const { data: authData } = await supabase.auth.getUser();
    const userId = authData?.user?.id;

    if (!userId) {
      logger.warn("Cannot record profile usage: No authenticated user found");
      return;
    }

    const { error } = await supabase
      .from('profile_overview_usage')
      .insert({
        model: 'gpt-4o-mini',
        user_id: userId
      });
    
    if (error) {
      logger.error("Error recording profile usage:", error);
    } else {
      logger.log("Profile usage recorded successfully");
    }
  } catch (err) {
    logger.error("Exception recording profile usage:", err);
  }
};

export const generateProfileOverviewApi = async (
  skills: string,
  experience: string,
  title: string = "",
  targetAudience: string = ""
): Promise<GenerateProfileResult> => {
  logger.log("Starting profile overview generation API call");
  
  try {
    // Show initial toast notification
    toast({
      title: "Connecting to AI service...",
      description: "This may take a few moments.",
      duration: 3000,
    });
    
    // Enhanced internet connectivity check
    logger.log("Checking internet connectivity");
    
    // Try multiple times with increasing timeouts
    let isConnected = false;
    
    // First quick check (3 second timeout)
    isConnected = await checkInternetConnection(3000);
    
    if (!isConnected) {
      logger.log("First connectivity check failed, trying with longer timeout");
      // Second check with longer timeout if first fails
      isConnected = await checkInternetConnection(8000);
    }
    
    // Handle no internet connection
    if (!isConnected) {
      logger.error("No internet connection detected after multiple attempts");
      
      toast({
        title: "Network Connection Error",
        description: "Unable to connect to the internet. Please check your connection and try again.",
        variant: "destructive",
      });
      
      return {
        overview: "",
        error: "No internet connection detected. Please check your network connection and try again.",
        isQuotaExceeded: false,
        isRateLimited: false
      };
    }
    
    logger.log("Internet connection confirmed. Proceeding with AI request.");
    
    // Function call with timeout and retry logic
    const timeoutDuration = 120000; // 2 minutes
    const maxRetries = 3;
    let lastError = null;
    let retryDelay = 2000; // Start with 2 seconds
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      // Only show retry toast after first attempt
      if (attempt > 0) {
        logger.log(`Retry attempt ${attempt} of ${maxRetries}`);
        toast({
          title: `Retry attempt ${attempt}`,
          description: "Still trying to connect to the AI service...",
          duration: 3000,
        });
      }
      
      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error("Request timed out after 2 minutes")), timeoutDuration)
      );
      
      logger.log("Sending profile overview request to Edge Function");
      
      // Prepare the function call with all parameters
      const functionCall = supabase.functions.invoke("generate-profile-overview", {
        body: { 
          skills: skills.trim(),
          experience: experience.trim(),
          title: title?.trim(),
          targetAudience: targetAudience?.trim()
        },
      });
      
      // Race between the timeout and the function call
      const { data, error } = await Promise.race([
        functionCall,
        timeoutPromise
      ]);

      if (error) {
        lastError = error;
        logger.error(`Attempt ${attempt + 1} failed with error:`, error);
        
        // Handle network errors specially
        const errorMessage = error.message || "";
        const isNetworkError = 
          errorMessage.includes("Failed to fetch") || 
          errorMessage.includes("NetworkError") ||
          errorMessage.includes("network") ||
          errorMessage.includes("Edge Function") ||
          errorMessage.includes("AbortError") ||
          errorMessage.includes("timed out");
        
        if (isNetworkError && attempt < maxRetries) {
          logger.log(`Network error detected, will retry in ${retryDelay / 1000}s`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));
          retryDelay *= 2; // Exponential backoff
          continue;
        }
        
        throw error;
      }

      // Check for errors in response data
      if (data.error) {
        logger.error("Profile generation error:", data.error);
        
        // Special case for connectivity error
        if (data.connectivity_error) {
          toast({
            title: "Service Connection Error",
            description: "Our servers are having trouble connecting to the AI service. Please try again later.",
            variant: "destructive",
          });
          
          return {
            overview: "",
            error: "Server connectivity error. Please try again later.",
            isQuotaExceeded: false,
            isRateLimited: !!data.rate_limited
          };
        }
        
        return {
          overview: "",
          error: data.error,
          isQuotaExceeded: !!data.quota_exceeded,
          isRateLimited: !!data.rate_limited,
        };
      }

      // Check we actually got an overview
      if (!data.overview) {
        throw new Error("No profile overview was generated. Please try again.");
      }

      // Record successful profile overview generation
      logger.log("Profile overview generated successfully with length:", data.overview.length);
      await recordProfileUsage();

      return {
        overview: data.overview,
        error: null,
        isQuotaExceeded: false,
        isRateLimited: false,
      };
    }
    
    // If we got here, all retries failed
    const errorMessage = lastError?.message || "All retry attempts failed";
    logger.error("All retries failed with error:", errorMessage);
    
    // Different messages based on error type
    if (errorMessage.includes("timed out")) {
      toast({
        title: "Request Timeout",
        description: "The AI service is taking too long to respond. Please try again later.",
        variant: "destructive",
      });
      
      return {
        overview: "",
        error: "Request timed out. The AI service might be busy. Please try again in a few moments.",
        isQuotaExceeded: false,
        isRateLimited: false,
      };
    } else if (
      errorMessage.includes("network") || 
      errorMessage.includes("fetch") || 
      errorMessage.includes("Unable to connect")
    ) {
      toast({
        title: "Network Connection Error",
        description: "Unable to connect to the AI service. Please check your internet connection.",
        variant: "destructive",
      });
      
      return {
        overview: "",
        error: "Network error: Unable to connect to AI service. Please check your internet connection.",
        isQuotaExceeded: false,
        isRateLimited: false,
      };
    }
    
    // Generic error
    return {
      overview: "",
      error: errorMessage,
      isQuotaExceeded: false,
      isRateLimited: false,
    };
  } catch (error) {
    logger.error("Error generating profile overview:", error);
    
    let errorMessage = "An unexpected error occurred. Please try again later.";
    
    if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      overview: "",
      error: errorMessage,
      isQuotaExceeded: false,
      isRateLimited: false,
    };
  }
};
